import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_COMPACT_ACTION_BUTTON_SX, FORM_IMAGE_CLEAR_BUTTON_SX as IMAGE_CLEAR_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import Pagination from '../../component/Pagination'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import EmbeddedTableContainer from '../../component/EmbeddedTableContainer'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, FORM_SUB_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    APP_SEASON_IMAGE_FILE_ACCEPT,
    APP_SEASON_IMAGE_FILE_ERROR_MESSAGE,
    applyAppSeasonDraft,
    clearAppSeasonDraft,
    createAppSeasonDraftPayload,
    createEditAppSeasonDraftKey,
    isAppSeasonImageFile,
    readAppSeasonDraft,
    saveAppSeasonDraft,
} from './appSeasonDraftUtils'
import {
    APP_SEASON_IMAGE_LEAVE_MESSAGE,
    hasPendingAppSeasonImage,
    usePendingAppSeasonImageUnloadGuard,
} from './appSeasonLeaveGuard'

const DEFAULT_SEARCH_SELECT = {
    searchType: '',
}

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({
    noticeId: '',
    title: '',
    content: '',
    startTime: new Date(),
    endTime: new Date(),
    meetType: 3,
    menuType: 2,
    menuTitle: '',
})

const IMAGE_PANEL_SX = FORM_PANEL_SX

const SETTING_GROUP_SX = {
    ...FORM_SUB_PANEL_SX,
}

const getChoiceCardSx = (selected, disabled = false) => ({
    ...getFormChoiceCardSx(selected, disabled),
    p: 1.5,
    width: '100%',
})

const MEET_TYPE_OPTIONS = [
    {
        value: 1,
        title: '거리전',
        description: '거리 누적 기준으로 시즌 이벤트를 운영합니다.',
    },
    {
        value: 3,
        title: '만보기',
        description: '걸음 수 기준으로 시즌 이벤트를 운영합니다.',
    },
]

const MENU_TYPE_OPTIONS = [
    {
        value: 1,
        title: '기본',
        description: '앱 기본 탭 타이틀을 사용합니다.',
    },
    {
        value: 2,
        title: '직접입력',
        description: '아래 탭 타이틀 입력값을 앱에 노출합니다.',
    },
]

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

// 라디오 옵션에 설명을 함께 표시해 선택 결과를 운영자가 바로 이해할 수 있게 한다.
function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

// 카드형 선택 UI에서도 기존 입력 변경 핸들러가 받던 name/value 이벤트 형태를 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function SeasonChoiceGroup({
    label,
    name,
    value,
    fallbackValue,
    options,
    disabled,
    onChange,
}) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {label}
            </Typography>
            <Box
                role="radiogroup"
                aria-label={label}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const selected = Number(value || fallbackValue) === option.value

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={String(option.value)}
                            control={(
                                <Radio
                                    name={name}
                                    checked={selected}
                                    onChange={() => onChange(createFieldChangeEvent(name, String(option.value)))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(selected, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

// 시즌 이미지를 미리보기, 업로드, 선택 취소까지 한 패널 안에서 관리한다.
function SeasonImageUploadPanel({
    imageSrc,
    imageFile,
    disabled,
    onUpload,
    hasPendingFile = false,
    onClear,
}) {
    const imageFileName = getImageFileName(imageFile)
    const fileInputRef = useRef(null)

    const handleClearClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }

        onClear?.()
    }

    return (
        <Paper variant="outlined" sx={IMAGE_PANEL_SX}>
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        App 시즌 이미지
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        App 시즌 화면에 노출되는 대표 이미지를 확인하고 교체합니다.
                    </Typography>
                </Box>
                {hasPendingFile && (
                    <Button
                        size="small"
                        color="error"
                        variant="text"
                        startIcon={<RemoveIcon />}
                        onClick={handleClearClick}
                        sx={IMAGE_CLEAR_BUTTON_SX}
                        disabled={disabled}
                    >
                        선택 취소
                    </Button>
                )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box
                    sx={{
                        ...FORM_IMAGE_PREVIEW_FRAME_SX,
                        width: '100%',
                        height: { xs: 220, md: 280 },
                    }}
                >
                    <Box
                        component="img"
                        src={imageSrc || '/images/noimage.jpg'}
                        alt="App 시즌 이미지 미리보기"
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        이미지 업로드
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                        JPG, PNG, GIF, WEBP 형식의 이미지를 업로드할 수 있습니다.
                    </Typography>
                    <FormControl fullWidth>
                        <TextField
                            inputProps={{ accept: APP_SEASON_IMAGE_FILE_ACCEPT }}
                            inputRef={fileInputRef}
                            style={{ display: 'none' }}
                            name="image"
                            id="image"
                            type="file"
                            disabled={disabled}
                            onChange={onUpload}
                        />
                        <Box component="label" htmlFor="image" sx={{ display: 'block' }}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                component="span"
                                startIcon={<Add />}
                                sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                                disabled={disabled}
                            >
                                이미지 선택
                            </Button>
                        </Box>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    )
}

function SeasonSettingPanel({ children }) {
    return (
        <Paper variant="outlined" sx={IMAGE_PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    시즌 설정
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    대회 종류와 이벤트전 탭 타이틀 노출 방식을 설정합니다.
                </Typography>
            </Box>
            {children}
        </Paper>
    )
}

function SettingGroup({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={SETTING_GROUP_SX}>
            <Box sx={{ mb: 1.25, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            {children}
        </Paper>
    )
}

export default function AppSeason(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.seasonId
    const listCurPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || DEFAULT_SEARCH_SELECT
    const listSearch = location.search || ''
    const draftKey = createEditAppSeasonDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [hasDetail, setHasDetail] = useState(false)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [image, setImage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [noticeParams, setNoticeParams] = useState([])
    const [curPage, setCurPage] = useState(1)
    const offset = 5
    const [totalCount, setTotalCount] = useState(0)
    const initialDraftSnapshotRef = useRef('')
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const originalImageRef = useRef({ image: '', preview: '' })
    const leaveGuardEnabled = hasPendingAppSeasonImage(params) && !saving && !navigationUnlocked

    usePendingAppSeasonImageUnloadGuard(leaveGuardEnabled)

    const getData = useCallback(async () => {
        try {
            const { data } = await API.post('/api/v1/admin/appNotice/getList', { page: curPage, offset: offset, searchSelect: searchSelect })
            if (data.code === 200) {
                setTotalCount(data.data.count)
                setNoticeParams(data.data.rows)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('공지사항 목록을 불러오지 못했습니다.')
        }
    }, [curPage, history, searchSelect])

    const chooseButtonClick = (noticeId) => {
        setParams((prev) => ({
            ...prev,
            noticeId: Number(noticeId)
        }))
    }

    const cancelButtonClick = () => {
        setParams((prev) => ({
            ...prev,
            noticeId: 0
        }))
    }

    useEffect(() => {
        getData()
    }, [getData])

    const getSeasonData = useCallback(async () => {
        if (!id) {
            const errorMessage = '필수 파라미터가 없어 App 별주부전 상세를 불러올 수 없습니다.'
            setParams(createDefaultParams())
            setImage('')
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        setLoading(true)
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appSeason/getDetail', { seasonId: id })
            if (data.code === 200) {
                const baseParams = data.data || {}

                if (!baseParams.seasonId) {
                    setParams(createDefaultParams())
                    setImage('')
                    setDetailErrorMessage('App 별주부전 상세 정보를 찾을 수 없습니다.')
                    setLoading(false)
                    return
                }

                const storedDraft = readAppSeasonDraft(draftKey)
                const restoredDraft = applyAppSeasonDraft({
                    baseParams,
                    draft: storedDraft,
                })

                initialDraftSnapshotRef.current = JSON.stringify(createAppSeasonDraftPayload({
                    params: baseParams,
                }))

                const previewImage = baseParams.image === null ? '/images/noimage.jpg' : `${process.env.REACT_APP_AWS_S3_URL}/${baseParams.image}`

                originalImageRef.current = {
                    image: baseParams.image,
                    preview: previewImage,
                }

                setParams(restoredDraft.params)
                setCurPage(restoredDraft.noticePage)
                setImage(previewImage)
                setHasDetail(true)
                setDetailErrorMessage('')
                setDraftReady(true)
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 별주부전 상세를 불러오지 못했습니다.'
                setParams(createDefaultParams())
                setImage('')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 별주부전 상세를 불러오지 못했습니다.'
            setParams(createDefaultParams())
            setImage('')
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [draftKey, history, id])

    useEffect(() => {
        getSeasonData()
    }, [getSeasonData])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // App 시즌 상세는 목록 query를 건드리지 않고 편집 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppSeasonDraftPayload({
            params,
            noticePage: curPage,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppSeasonDraft(draftKey)
            return
        }

        saveAppSeasonDraft(draftKey, nextDraft)
    }, [curPage, draftKey, draftReady, hasDetail, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        clearAppSeasonDraft(draftKey)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 조건은 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: `/appSeasons`,
                search: listSearch,
                curPage: listCurPage,
                searchText: searchText,
                searchSelect: searchSelect,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {}),
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (!leaveGuardEnabled) {
            clearAppSeasonDraft(draftKey)
            return
        }

        event.preventDefault()

        if (!window.confirm(APP_SEASON_IMAGE_LEAVE_MESSAGE)) {
            return
        }

        moveToList()
    }

    const handleUpdateField = (e) => {
        setParams((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const handleUploadClick = (e) => {
        e.preventDefault()
        const { name, files } = e.target
        const input = e.target
        const file = files[0]

        if (!file) {
            return
        }

        if (!isAppSeasonImageFile(file)) {
            input.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(APP_SEASON_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        const reader = new FileReader()

        reader.onloadend = () => {
            setParams((prev) => ({
                ...prev,
                [name]: file
            }))

            setImage(reader.result)
        }
    
        reader.readAsDataURL(file)
    }

    const handleClearSelectedImage = () => {
        setParams((prev) => ({
            ...prev,
            image: originalImageRef.current.image,
        }))
        setImage(originalImageRef.current.preview)
    }

    const setDateField = (name, date) => {
        setParams((prev) => ({
            ...prev,
            [name]: date
        }))
    }

    const updateData = async (formData) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/appSeason/update',
                data: formData,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 200) {
                // 저장 후에도 운영자가 보던 App 시즌 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 별주부전을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextTitle = (params.title || '').trim()
        const nextContent = (params.content || '').trim()
        const nextMenuTitle = (params.menuTitle || '').trim()

        setParams((prev) => ({
            ...prev,
            title: nextTitle,
            content: nextContent,
            menuTitle: nextMenuTitle
        }))

        if (!params.noticeId) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('공지사항을 선택해주세요.')
            return
        }

        if (!params.image) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('이미지를 업로드해주세요.')
            return
        }

        if (!nextTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('제목을 입력하세요.')
            return
        }

        if (!nextContent) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('내용을 입력하세요.')
            return
        }

        if (!params.startTime || !params.endTime) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('일정을 입력하세요.')
            return
        }

        if (Number(params.menuType) === 2 && !nextMenuTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('탭 타이틀을 입력하세요.')
            return
        }

        const formData = new FormData()
        formData.append('seasonId', id)
        formData.append('image', params.image)
        formData.append('noticeId', params.noticeId)
        formData.append('title', nextTitle)
        formData.append('content', nextContent)
        formData.append('startTime', params.startTime)
        formData.append('endTime', params.endTime)
        formData.append('meetType', params.meetType)
        formData.append('menuType', params.menuType)
        formData.append('menuTitle', nextMenuTitle)

        updateData(formData)
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="App 별주부전 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appSeasons" search={listSearch} curPage={listCurPage} visible={false} searchText={searchText} searchSelect={searchSelect} onBackClick={() => clearAppSeasonDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>App 별주부전 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>App 별주부전 수정</Title>
                <Prompt when={leaveGuardEnabled} message={APP_SEASON_IMAGE_LEAVE_MESSAGE} />
                <Paper sx={{ p: 2, opacity: saving ? 0.5 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                    <EmbeddedTableContainer>
                    <Table size="small" sx={{ minWidth: 800 }}>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>제목</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {noticeParams.map((row) => (
                            <TableRow key={row.noticeId}>
                                <TableCell>{row.rownum}</TableCell>
                                <TableCell>{row.title}</TableCell>
                                <TableCell align="right">
                                    {params.noticeId !== row.noticeId ?
                                        <Button variant="outlined" color="secondary" size="small" sx={FORM_COMPACT_ACTION_BUTTON_SX} onClick={() => chooseButtonClick(row.noticeId)} disabled={saving}>선택</Button>
                                    :
                                        <Button variant="outlined" color="secondary" size="small" sx={FORM_COMPACT_ACTION_BUTTON_SX} onClick={() => cancelButtonClick()} disabled={saving}>선택 해제</Button>
                                    }
                                </TableCell>
                            </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    </EmbeddedTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                </Paper>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} >
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="공지사항ID"
                                    id="noticeId"
                                    name="noticeId"
                                    value={params.noticeId || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                    InputProps={{ readOnly: true }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <SeasonImageUploadPanel
                                imageSrc={image}
                                imageFile={params.image}
                                disabled={saving}
                                onUpload={handleUploadClick}
                                hasPendingFile={params.image instanceof File}
                                onClear={handleClearSelectedImage}
                            />
                        </Grid>
                        <Grid item xs={6} md={6} sm={12}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                    <DesktopDatePicker
                                        required
                                        inputFormat="yyyy-MM-dd"
                                        mask={"____-__-__"}
                                        label="StartTime"
                                        value={params.startTime}
                                        onChange={(date) => setDateField('startTime', date)}
                                        renderInput={(params) => <TextField required {...params} size="small" disabled={saving}/>}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={6} sm={12}>
                            <FormControl fullWidth>
                                <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                    <DesktopDatePicker
                                        inputFormat="yyyy-MM-dd"
                                        mask={"____-__-__"}
                                        label="EndTime"
                                        value={params.endTime}
                                        onChange={(date) => setDateField('endTime', date)}
                                        renderInput={(params) => <TextField required {...params} size="small" disabled={saving} />}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="제목"
                                    id="title"
                                    name="title"
                                    value={params.title || ''}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="내용"
                                    multiline
                                    rows={5}
                                    id="content"
                                    name="content"
                                    value={params.content || ''}
                                    onChange={handleUpdateField}
                                    disabled={saving}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <SeasonSettingPanel>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} md={6}>
                                        <SettingGroup
                                            title="대회종류"
                                            description="시즌 이벤트 집계 기준을 선택합니다."
                                        >
                                            <SeasonChoiceGroup
                                                label="집계 기준"
                                                name="meetType"
                                                value={params.meetType}
                                                fallbackValue={3}
                                                options={MEET_TYPE_OPTIONS}
                                                disabled={saving}
                                                onChange={handleUpdateField}
                                            />
                                        </SettingGroup>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <SettingGroup
                                            title="이벤트전 탭 타이틀"
                                            description="앱에 표시할 탭 타이틀 방식을 선택합니다."
                                        >
                                            <SeasonChoiceGroup
                                                label="노출 방식"
                                                name="menuType"
                                                value={params.menuType}
                                                fallbackValue={2}
                                                options={MENU_TYPE_OPTIONS}
                                                disabled={saving}
                                                onChange={handleUpdateField}
                                            />
                                        </SettingGroup>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                label="탭 타이틀"
                                                id="menuTitle"
                                                name="menuTitle"
                                                value={params.menuTitle || ''}
                                                onChange={handleUpdateField}
                                                size="small"
                                                disabled={saving || Number(params.menuType) !== 2}
                                                required={Number(params.menuType) === 2}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </SeasonSettingPanel>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup path="/appSeasons" search={listSearch} curPage={listCurPage} visible={true} searchText={searchText} searchSelect={searchSelect} submitDisabled={saving} submitLabel={saving ? '저장 중...' : '저장'} onBackClick={handleBackClick}/>
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
